import { useEffect, useState } from 'react';
import { Alert } from '@msarat/theme/components';

import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.jsx';

/**
 * Choosing a curriculum.
 *
 * A parent no longer uploads a book. They narrow down to their child's
 * curriculum — country, then education track, then grade, then subject — pick
 * one we have already analysed, and assign it to a child.
 *
 * The change is not just about who does the work. Under the old flow a parent
 * uploaded a PDF and then waited, sometimes to be told their scan was
 * unreadable or their file too large. Those are our problems to solve, in a
 * console, once per textbook — not a wall for a parent to hit at the moment
 * they were trying to start.
 *
 * The filters only offer values the catalogue actually has. An option that
 * leads to an empty list reads as a broken product rather than as a curriculum
 * we have not added yet.
 */
export function Curriculum({ active, students }) {
  const { t } = useI18n();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [browsing, setBrowsing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  async function refresh() {
    setLoading(true);
    try {
      const result = await api.listCurricula();
      setAssignments(result.items ?? []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  if (!active) {
    return (
      <section className="panel">
        <h2 style={{ fontSize: 'var(--text-xl)' }}>{t('nav.curriculum')}</h2>
        <p className="empty">{t('curriculum.body')}</p>
        <Alert kind="error">{t('curriculum.blocked')}</Alert>
      </section>
    );
  }

  if (students.length === 0) {
    return (
      <section className="panel">
        <h2 style={{ fontSize: 'var(--text-xl)' }}>{t('nav.curriculum')}</h2>
        <p className="empty">{t('curriculum.needStudent')}</p>
      </section>
    );
  }

  return (
    <section className="panel">
      <div className="panel__head">
        <h2 style={{ fontSize: 'var(--text-xl)' }}>{t('nav.curriculum')}</h2>
        {!browsing ? (
          <button className="button button--primary" type="button" onClick={() => setBrowsing(true)}>
            {t('curriculum.browse')}
          </button>
        ) : null}
      </div>

      <Alert kind="error">{error}</Alert>
      {notice ? <Alert kind="success">{notice}</Alert> : null}

      {browsing ? (
        <Browser
          students={students}
          onCancel={() => setBrowsing(false)}
          onAssigned={() => {
            setBrowsing(false);
            setNotice(t('curriculum.assigned'));
            refresh();
          }}
        />
      ) : null}

      {loading ? (
        <p className="empty" aria-live="polite">
          {t('common.loading')}
        </p>
      ) : assignments.length === 0 && !browsing ? (
        <p className="empty">{t('curriculum.none')}</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {assignments.map((assignment) => (
            <AssignmentRow
              key={assignment.id}
              assignment={assignment}
              onRemoved={() => {
                setNotice('');
                refresh();
              }}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/** One curriculum a child is studying. */
function AssignmentRow({ assignment, onRemoved }) {
  const { t } = useI18n();
  const [pending, setPending] = useState(false);

  return (
    <li className="skill-row" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ minWidth: '16rem' }}>
        <strong>{assignment.title}</strong>
        <small>
          {t('curriculum.for', { name: assignment.student_name })} ·{' '}
          {t(`country.${assignment.country}`)} · {t(`track.${assignment.track}`)} ·{' '}
          {t('curriculum.grade')} {assignment.grade} · {t(`subject.${assignment.subject}`)}
          {assignment.lessons ? ` · ${t('curriculum.lessons', { n: assignment.lessons })}` : ''}
        </small>
      </div>
      <button
        className="button"
        type="button"
        disabled={pending}
        onClick={async () => {
          setPending(true);
          try {
            await api.unassignCurriculum(assignment.id);
            onRemoved();
          } finally {
            setPending(false);
          }
        }}
      >
        {t('curriculum.remove')}
      </button>
    </li>
  );
}

/**
 * The catalogue browser.
 *
 * Four narrowing choices in the order a parent thinks in — "we're in Egypt, on
 * the American diploma, grade six, maths" — and then the curricula that match.
 */
function Browser({ students, onCancel, onAssigned }) {
  const { t } = useI18n();
  const [filters, setFilters] = useState({ country: '', track: '', grade: '', subject: '' });
  const [data, setData] = useState(null);
  const [studentId, setStudentId] = useState(students[0]?.id ?? '');
  const [pending, setPending] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    api
      .browseCatalogue(filters)
      .then((result) => live && setData(result))
      .catch((err) => live && setError(err.message));
    return () => {
      live = false;
    };
  }, [filters.country, filters.track, filters.grade, filters.subject]);

  const set = (key) => (event) => setFilters((f) => ({ ...f, [key]: event.target.value }));

  async function assign(bookId) {
    setPending(bookId);
    setError('');
    try {
      await api.assignCurriculum(studentId, bookId);
      onAssigned();
    } catch (err) {
      setError(err.message);
    } finally {
      setPending('');
    }
  }

  const facets = data?.facets ?? { country: [], track: [], grade: [], subject: [] };

  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <h3 style={{ fontSize: 'var(--text-md)' }}>{t('curriculum.choose')}</h3>

      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <Select
          label={t('curriculum.forStudent')}
          value={studentId}
          onChange={(event) => setStudentId(event.target.value)}
          options={students.map((student) => [student.id, student.name])}
        />
        <Select
          label={t('curriculum.country')}
          value={filters.country}
          onChange={set('country')}
          options={facets.country.map((value) => [value, t(`country.${value}`)])}
        />
        <Select
          label={t('curriculum.track')}
          value={filters.track}
          onChange={set('track')}
          options={facets.track.map((value) => [value, t(`track.${value}`)])}
        />
        <Select
          label={t('curriculum.grade')}
          value={filters.grade}
          onChange={set('grade')}
          options={facets.grade.map((value) => [value, value])}
        />
        <Select
          label={t('curriculum.subject')}
          value={filters.subject}
          onChange={set('subject')}
          options={facets.subject.map((value) => [value, t(`subject.${value}`)])}
        />
      </div>

      {error ? <Alert kind="error">{error}</Alert> : null}

      {!data ? (
        <p className="empty">{t('common.loading')}</p>
      ) : data.items.length === 0 ? (
        <>
          <p className="empty">{t('curriculum.noneMatch')}</p>
          <p className="field__hint">{t('curriculum.notYet')}</p>
        </>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {data.items.map((entry) => (
            <li key={entry.id} className="skill-row" style={{ flexWrap: 'wrap' }}>
              <div style={{ minWidth: '16rem' }}>
                <strong>{entry.title}</strong>
                <small>
                  {t(`country.${entry.country}`)} · {t(`track.${entry.track}`)} ·{' '}
                  {t('curriculum.grade')} {entry.grade} · {t(`subject.${entry.subject}`)}
                  {entry.lessons ? ` · ${t('curriculum.lessons', { n: entry.lessons })}` : ''}
                </small>
              </div>
              <button
                className="button button--primary"
                type="button"
                disabled={Boolean(pending) || !studentId}
                onClick={() => assign(entry.id)}
              >
                {pending === entry.id ? t('curriculum.assigning') : t('curriculum.assign')}
              </button>
            </li>
          ))}
        </ul>
      )}

      <button className="button" type="button" onClick={onCancel} style={{ marginTop: 'var(--space-3)' }}>
        {t('curriculum.cancel')}
      </button>
    </div>
  );
}

/** A labelled select whose first option means "no filter". */
function Select({ label, value, onChange, options }) {
  const { t } = useI18n();
  return (
    <label className="field" style={{ minWidth: '9rem' }}>
      <span className="field__label">{label}</span>
      <select className="input" value={value} onChange={onChange}>
        <option value="">{t('curriculum.choose')}</option>
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </label>
  );
}
